import React, { useState, useEffect } from 'react';

const PointsTransactionsTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('transactionDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    partnerId: '',
    ptsUserId: '',
    type: '',
    minAmount: '',
    maxAmount: '',
    startDate: '',
    endDate: ''
  });
  
  // Fetch transactions when page, filters or sort changes
  useEffect(() => {
    fetchTransactions();
  }, [currentPage, itemsPerPage, sortField, sortOrder]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare query parameters
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        sort: sortField,
        order: sortOrder
      });

      // Add filters if they exist
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/points/transactions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch points transactions');
      }

      const data = await response.json();
      setTransactions(data.data);
      setTotalRecords(data.pagination.total);
    } catch (err) {
      console.error('Error fetching points transactions:', err);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for a new field
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchTransactions();
  };

  const handleClearFilters = () => {
    setFilters({
      partnerId: '',
      ptsUserId: '',
      type: '',
      minAmount: '',
      maxAmount: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
    setSortField('transactionDate');
    setSortOrder('desc');
    fetchTransactions();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get transaction type badge style
  const getTypeBadgeStyle = (type) => {
    let color;
    switch (type) {
      case 'earn':
        color = '#4CAF50'; // Green
        break;
      case 'spend':
        color = '#F44336'; // Red
        break;
      case 'referral':
        color = '#2196F3'; // Blue
        break;
      case 'expire':
        color = '#FF9800'; // Orange
        break;
      case 'adjust':
        color = '#9C27B0'; // Purple
        break;
      default:
        color = '#9E9E9E'; // Gray
    }

    return {
      backgroundColor: color,
      color: 'white',
      borderRadius: '12px',
      padding: '3px 8px',
      fontSize: '12px',
      display: 'inline-block'
    };
  };

  // Generate page numbers
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      <h3>Points Transactions</h3>
      
      {/* Filters */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
        <form onSubmit={handleApplyFilters}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Partner:</label>
              <input
                type="text"
                name="partnerId"
                value={filters.partnerId}
                onChange={handleFilterChange}
                placeholder="Partner ID"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>User:</label>
              <input
                type="text"
                name="ptsUserId"
                value={filters.ptsUserId}
                onChange={handleFilterChange}
                placeholder="Points User ID"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Type:</label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              >
                <option value="">All</option>
                <option value="earn">Earn</option>
                <option value="spend">Spend</option>
                <option value="referral">Referral</option>
                <option value="expire">Expire</option>
                <option value="adjust">Adjust</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Min Amount:</label>
              <input
                type="number"
                name="minAmount"
                value={filters.minAmount}
                onChange={handleFilterChange}
                placeholder="Min"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Max Amount:</label>
              <input
                type="number"
                name="maxAmount"
                value={filters.maxAmount}
                onChange={handleFilterChange}
                placeholder="Max"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Start Date:</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>End Date:</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Apply Filters
              </button>
              
              <button
                type="button"
                onClick={handleClearFilters}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Transactions Table */}
      {loading ? (
        <div>Loading transactions...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('_id')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      ID
                      {sortField === '_id' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('transactionDate')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Date
                      {sortField === 'transactionDate' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('type')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Type
                      {sortField === 'type' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('amount')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Amount
                      {sortField === 'amount' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('balanceAfter')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Balance After
                      {sortField === 'balanceAfter' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>User ID</th>
                  <th style={tableHeaderStyle}>Partner</th>
                  <th style={tableHeaderStyle}>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px 0' }}>
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map(transaction => (
                    <tr key={transaction._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={tableCellStyle}>{transaction._id.substring(0, 8)}...</td>
                      <td style={tableCellStyle}>{formatDate(transaction.transactionDate)}</td>
                      <td style={tableCellStyle}>
                        <span style={getTypeBadgeStyle(transaction.type)}>
                          {transaction.type}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{ color: transaction.amount >= 0 ? '#4CAF50' : '#F44336' }}>
                          {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                        </span>
                      </td>
                      <td style={tableCellStyle}>{transaction.balanceAfter.toLocaleString()}</td>
                      <td style={tableCellStyle}>
                        {transaction.ptsUserId?._id || transaction.ptsUserId?.substring(0, 8) + '...' || 'N/A'}
                      </td>
                      <td style={tableCellStyle}>
                        {transaction.partnerId?.name || transaction.partnerId?.substring(0, 8) + '...' || 'N/A'}
                      </td>
                      <td style={tableCellStyle}>{transaction.description}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', alignItems: 'center' }}>
            <div>
              Showing {transactions.length} of {totalRecords} entries
            </div>
            <div>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '5px 10px',
                  marginRight: '5px',
                  backgroundColor: currentPage === 1 ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              
              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  style={{
                    padding: '5px 10px',
                    marginRight: '5px',
                    backgroundColor: currentPage === number ? '#4f46e5' : '#f3f4f6',
                    color: currentPage === number ? 'white' : 'black',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '5px 10px',
                  backgroundColor: currentPage === totalPages ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Styles
const tableHeaderStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  borderBottom: '2px solid #d1d5db',
  fontSize: '14px'
};

const tableCellStyle = {
  padding: '12px 15px',
  fontSize: '14px'
};

export default PointsTransactionsTab;